/// <amd-module name="Build/DesignerExtensions/YamlSnippetForm/Components/EnhancedLabel" />
import * as React from "react";

import { css } from "VSSUI/Util";
import { Callout } from "VSSUI/Callout";
import { Location } from "VSSUI/Utilities/Position";
import { Icon } from "VSSUI/Icon";
import { KeyCode } from "VSSUI/Util";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";

import { ScreenSize } from "VSS/Core/Util/Screen";
import { MarkdownRendererComponent } from "VSS/Features/Markdown/MarkdownRendererComponent";
import * as Resources from "Build/DesignerExtensions/YamlSnippetForm/Resources";

export interface IEnhancedLabelProps {
    /**
     * A title for the label.
     */
    title: string;

    /**
     * Optional className to include on the title.
     */
    titleClassName?: string;

    /**
     * Optional className to include on the element that wraps the item's
     */
    className?: string;

    /**
     * If true, renders a red star next to the title
     */
    isRequired?: boolean;

    /**
     * If provided, an info button will appear to the right of the title 
     * and the content will be rendered in a callout. Documentation string can use markdown.
     */
    documentation?: string;

    /**
     * label that can be added to the icon to help assistive technology understand
     * the meaning of the icon.
     */
    ariaLabel?: string;
}

interface IEnhancedLabelState {
    showCallout: boolean;
    anchorElement?: HTMLElement;
}

let enhancedlabelCalloutId = 1;

export class EnhancedLabel extends React.Component<IEnhancedLabelProps, IEnhancedLabelState> {
    private _calloutId = `enhancedlabelcallout-${enhancedlabelCalloutId++}`;
    private _blockCallout: boolean;

    public constructor(props: IEnhancedLabelProps) {
        super(props);

        this.state = {
            showCallout: false
        };
    }
    
    public render() {
        return <span className={css("enhancedlabel inline-flex-row", this.props.className)}>
            <span className={css(this.props.isRequired && "enhancedlabel-required", this.props.titleClassName)}>{this.props.title}</span>
            {this.props.documentation &&
                <span className={"enhancedlabel-documentation"} 
                     tabIndex={0} 
                     onClick={this._onClick} 
                     onMouseDown={this._onMouseDown}
                     onKeyPress={this._onKeypress} 
                     role={"button"} 
                     aria-label={this.props.ariaLabel || Resources.InformationButton}>
                        <Icon className={"no-padding"} iconName={"Info"} />
                </span>
            }
            {this.state.showCallout && this.state.anchorElement &&
                <ScreenSizeObserver>
                    {(screenSizeProps: { screenSize: ScreenSize }) => {
                        const isSmall = screenSizeProps.screenSize <= ScreenSize.small;
                        const offset = isSmall ? 0 : 8;
                        return <Callout
                            className={"enhancedlabel-documentation-callout"}
                            anchorElement={this.state.anchorElement}
                            anchorOffset={{ vertical: offset, horizontal: offset }}
                            anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                            calloutOrigin={{ horizontal: Location.center, vertical: Location.start }}
                            id={this._calloutId}
                            key={this._calloutId}
                            role="tooltip"
                            onDismiss={this._hideCallout}
                            blurDismiss={true}
                            escDismiss={true}
                            focuszoneProps={{
                                focusOnMount: true,
                                defaultActiveElement: ".enhancedlabel-documentation-callout-focusbox",
                                circularNavigation: true
                            }} >
                            <div className={"enhancedlabel-documentation-callout-focusbox"} tabIndex={0}>
                                <MarkdownRendererComponent className={"enhancedlabel-documentation-callout-content depth-16"} rawContent={this.props.documentation!}
                                    options={{
                                        breaks: true,
                                        html: true,
                                        hideExternalImageIcon: true
                                    }} />
                            </div>
                        </Callout>
                    }}
                </ScreenSizeObserver>}
        </span>;
    }
    
    private _showCallout = (event: React.SyntheticEvent<HTMLSpanElement>) => {
        this.setState({
            showCallout: true,
            anchorElement: event.currentTarget
        });
    }

    private _hideCallout = () => {
        this.setState({
            showCallout: false
        })
    }

    private _onClick = (event: React.SyntheticEvent<HTMLSpanElement>) => {
        if (!this._blockCallout) {
            this._showCallout(event);
        }
    }

    private _onKeypress = (event: React.KeyboardEvent<HTMLSpanElement>) => {
        if (event.which === KeyCode.enter || event.which === KeyCode.space) {
            this._showCallout(event);
        }
    }

    private _onMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
        // The callout disappears on blur but then this icon button gets the click event and immediately wants to re-show it
        // Clicking on the info button a second time should act as a dismiss rather than flicker it
        this._blockCallout = this.state.showCallout;
    }
}
